import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout/PageLayout';
import { Button, Input, Card, useToast, Form, FormGroup, FormLabel } from '@/components/ui';
import { useAdminUserActions } from './hooks/useUsers';
import { adminUsersService, ROLE_MAP } from './services/users.service';
import { companiesService } from '../companies/services/companies.service';
import { useQuery } from '@tanstack/react-query';
import { PermissionPresetSelector } from './components/PermissionPresetSelector';
import { getRoleIcon } from '@/utils/role';
import { useAuth } from '@/core/auth/AuthContext';

export default function AdminUserFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { updateUser } = useAdminUserActions();
  const { profile } = useAuth();
  
  const isEdit = !!id;
  const isSuperAdmin = profile?.role_code === 'super_admin';

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    job_title: '',
    company_id: isSuperAdmin ? '' : (profile?.company_id || ''),
    role_code: 'staff',
    is_active: true,
  });
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  // Fetch user if editing
  const { data: userRes, isLoading: isFetchingUser } = useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => adminUsersService.getById(id!),
    enabled: isEdit,
  });

  // Fetch companies for dropdown
  const { data: companiesRes } = useQuery({
    queryKey: ['admin-companies-list'],
    queryFn: () => companiesService.list({ pageSize: 100 })
  });

  useEffect(() => {
    if (userRes?.data) {
      setFormData({
        full_name: userRes.data.full_name || '',
        email: userRes.data.email || '',
        phone: userRes.data.phone || '',
        job_title: userRes.data.job_title || '',
        company_id: userRes.data.company_id || '',
        role_code: userRes.data.role_code || 'staff',
        is_active: userRes.data.is_active ?? true,
      });
    }
  }, [userRes]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const handleToggleActive = () => {
    setFormData(prev => ({ ...prev, is_active: !prev.is_active }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isEdit) {
        await updateUser({ id: id!, updates: { ...formData, permissions: selectedPermissions } });
        navigate(`/admin/users/detail/${id}`);
      } else {
        const res = await adminUsersService.create({ ...formData, permissions: selectedPermissions });
        if (res.error) throw new Error(res.error);
        toast.success('Khởi tạo định danh thành công');
        navigate(`/admin/users/detail/${res.data.id}`);
      }
    } catch (error: any) {
      toast.error(error.message || 'Có lỗi xảy ra khi xử lý yêu cầu');
    } finally {
      setIsLoading(false);
    }
  };

  if (isEdit && isFetchingUser) {
    return (
      <PageLayout title="Đang nạp Dữ liệu..." description="Vui lòng chờ trong giây lát">
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title={
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600 border border-brand-100 shadow-sm">
            <i className={isEdit ? "ri-user-settings-line text-xl" : "ri-user-add-line text-xl"} />
          </div>
          <span className="text-xl font-medium tracking-tight text-slate-900">
            {isEdit ? `Hiệu chỉnh: ${formData.full_name}` : 'Cấp phát Định danh Mới'}
          </span>
        </div>
      }
      description={
        <span className="text-sm text-slate-500 font-medium ml-14">
          {isEdit ? 'Quản lý thông tin cốt lõi và quyền truy cập' : 'Khởi tạo tài khoản và thiết lập không gian làm việc an toàn'}
        </span>
      }
    >
      <Form onSubmit={handleSubmit} className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        
        {/* Full Width Block: Gói Quyền Mẫu */}
        <Card className="p-6 rounded-2xl border-slate-200/60 shadow-sm">
          <PermissionPresetSelector 
            selectedPermissions={selectedPermissions}
            onChange={setSelectedPermissions}
          />
        </Card>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Cột 1: Thông tin Lõi & Bảo mật (Tự động co giãn) */}
          <div className="flex-1 min-w-0 space-y-6">
            <Card className="p-7 rounded-2xl border-slate-200/60 shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center gap-2.5 mb-8 pb-4 border-b border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                  <i className="ri-fingerprint-line text-lg" />
                </div>
              <h3 className="text-base font-medium text-slate-800 tracking-tight">Định danh Lõi (Core Identity)</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-1">
                <Input
                  label="Họ và tên"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Nhập họ và tên đầy đủ..."
                  required
                />
              </div>
              <div className="relative">
                <Input
                  label="Địa chỉ Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@company.com"
                  required
                  disabled={isEdit}
                />
                {isEdit && (
                  <div className="absolute right-3 top-9 text-slate-400 group cursor-help">
                    <i className="ri-lock-2-line" />
                    <div className="absolute hidden group-hover:block w-48 p-2 bg-slate-800 text-white text-xs rounded-lg -top-10 left-1/2 -translate-x-1/2 z-10 shadow-lg text-center">
                      Email định danh không thể thay đổi
                    </div>
                  </div>
                )}
              </div>
              <Input
                label="Số điện thoại"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="09xx xxx xxx"
              />
              <div className="md:col-span-2">
                <Input
                  label="Chức danh (Nội bộ)"
                  name="job_title"
                  value={formData.job_title}
                  onChange={handleChange}
                  placeholder="VD: Chuyên viên Phân tích, Kỹ sư Cảnh quan..."
                />
              </div>
            </div>
          </Card>

          <Card className="p-7 rounded-2xl border-slate-200/60 shadow-sm">
            <div className="flex items-center gap-2.5 mb-6">
              <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                <i className="ri-shield-keyhole-line text-lg" />
              </div>
              <h3 className="text-base font-medium text-slate-800 tracking-tight">Cơ chế Bảo mật (Security Settings)</h3>
            </div>
            <div className="p-4 bg-amber-50/50 rounded-xl border border-amber-100 flex items-start gap-3">
              <i className="ri-error-warning-line text-amber-600 text-lg mt-0.5" />
              <div className="text-sm font-medium text-amber-800/90 leading-relaxed">
                {!isEdit ? (
                  <p>Hệ thống sử dụng cơ chế <strong className="text-amber-900">Mật khẩu Tự động Sinh (Auto-generated Password)</strong>. Thông tin xác thực sẽ được mã hóa và gửi trực tiếp tới email định danh của người dùng sau khi khởi tạo thành công nhằm đảm bảo an ninh tối đa.</p>
                ) : (
                  <p>Không thể sửa đổi mật khẩu từ màn hình này. Vui lòng sử dụng chức năng <strong>Khôi phục Mật khẩu (Reset Password)</strong> trong bảng điều khiển hoặc yêu cầu người dùng tự đặt lại qua luồng Quên mật khẩu.</p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Cột 2: Bảng Điều khiển Phân quyền (Kích thước cố định) */}
        <div className="w-full lg:w-[420px] shrink-0 space-y-6">
          <Card className="p-6 rounded-2xl border-slate-200/60 shadow-sm">
            <div className="flex items-center gap-2.5 mb-6 pb-4 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <i className="ri-key-2-line text-lg" />
              </div>
              <h3 className="text-base font-medium text-slate-800 tracking-tight">Trạm Kiểm soát Truy cập</h3>
            </div>

            <div className="space-y-5">
              {isSuperAdmin && (
                <FormGroup>
                  <FormLabel className="text-xs text-slate-500" required>Tổ chức / Công ty cấp phát</FormLabel>
                  <div className="relative">
                    <select
                      name="company_id"
                      required
                      className="w-full h-11 pl-10 pr-4 bg-slate-50/50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all cursor-pointer hover:bg-white hover:border-brand-300 appearance-none"
                      value={formData.company_id || ''}
                      onChange={handleChange}
                    >
                      <option value="" disabled>-- Bắt buộc chọn Tổ chức --</option>
                      {companiesRes?.data?.map(c => (
                        <option key={c.id} value={c.id}>🏢 {c.name}</option>
                      ))}
                    </select>
                    <i className="ri-building-4-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </FormGroup>
              )}

              <FormGroup>
                <FormLabel className="text-xs text-slate-500" required>Cấp độ Quyền hạn (Role)</FormLabel>
                <div className="relative">
                  <select
                    name="role_code"
                    className="w-full h-11 pl-10 pr-4 bg-slate-50/50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all cursor-pointer hover:bg-white hover:border-brand-300 appearance-none"
                    value={formData.role_code || 'staff'}
                    onChange={handleChange}
                    required
                  >
                    {Object.entries(ROLE_MAP).map(([code, name]) => (
                      <option key={code} value={code}>{name}</option>
                    ))}
                  </select>
                  <i className={`${getRoleIcon(formData.role_code)} absolute left-3 top-1/2 -translate-y-1/2 text-slate-400`} />
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </FormGroup>

              {/* Toggle Switch Tùy chỉnh */}
              <div className="p-4 bg-slate-50/50 rounded-xl border border-slate-100 mt-2">
                <div className="flex items-center justify-between cursor-pointer" onClick={handleToggleActive}>
                  <div>
                    <p className="text-sm font-medium text-slate-800">Trạng thái Hoạt động</p>
                    <p className="text-xs font-medium text-slate-400 mt-0.5">{formData.is_active ? 'Cho phép truy cập hệ thống' : 'Tạm thời vô hiệu hóa'}</p>
                  </div>
                  <div className={`relative w-12 h-6 rounded-full transition-colors duration-300 ease-in-out ${formData.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                    <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform duration-300 shadow-sm ${formData.is_active ? 'translate-x-6' : 'translate-x-0'}`} />
                  </div>
                </div>
              </div>
            </div>
          </Card>


          <div className="grid grid-cols-2 gap-3 sticky bottom-6 z-10 bg-white/80 backdrop-blur-md p-1 -mx-1 rounded-xl">
            <Button
              type="button"
              variant="ghost"
              className="w-full h-12 rounded-xl font-medium bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 shadow-sm"
              onClick={() => navigate(isEdit ? `/admin/users/detail/${id}` : '/admin/users')}
            >
              Hủy bỏ
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="w-full h-12 rounded-xl font-medium shadow-lg shadow-brand-500/25 transition-all hover:shadow-brand-500/40 hover:-translate-y-0.5"
              isLoading={isLoading}
            >
              <i className={isEdit ? "ri-save-3-line mr-2" : "ri-check-line mr-2"} />
              {isEdit ? 'Lưu' : 'Khởi tạo'}
            </Button>
          </div>
        </div>
      </div>
      </Form>
    </PageLayout>
  );
}
